import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid from '../../common/FormGrid';
import { FaPlus, FaShoppingCart, FaSave, FaTimes, FaTrash, FaSearch, FaEdit, FaDollarSign, FaEye, FaFilter, FaEraser } from 'react-icons/fa';
import { getApiUrl } from '../../../utils/api';
import '../../Inventario/catalogos/CatalogoProveedores.css';

// Select con buscador integrado
const SearchableSelect = ({ options, value, onChange, placeholder = 'Buscar...', style = {} }) => {
  const [abierto, setAbierto] = React.useState(false);
  const [texto, setTexto] = React.useState('');
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0, width: 0 });
  const ref = React.useRef(null);
  const triggerRef = React.useRef(null)

  const seleccionado = options.find(o => String(o.value) === String(value));

  const filtradas = options.filter(o =>
    o.label.toLowerCase().includes(texto.toLowerCase())
  );

  // Calcular posición del dropdown al abrir
  const abrirDropdown = () => {
    if (!abierto && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
    setAbierto(!abierto);
    setTexto('');
  };

  // Cerrar al hacer click fuera (checa tanto el trigger como el portal)
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // Verificar si el click fue en el portal del dropdown
        const portalEl = ref.current.__portalEl;
        if (portalEl && portalEl.contains(e.target)) return;
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <div
        ref={triggerRef}
        onClick={abrirDropdown}
        style={{
          padding: '4px 8px', border: '1px solid #ccc', borderRadius: '3px',
          fontSize: '12px', background: 'white', cursor: 'pointer', minHeight: '26px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          color: seleccionado ? '#333' : '#999'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {seleccionado ? seleccionado.label : placeholder}
        </span>
        <span style={{ fontSize: '9px', marginLeft: '4px' }}>▼</span>
      </div>
      {abierto && ReactDOM.createPortal(
        <div
          ref={(el) => { if (el) ref.current.__portalEl = el; }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: dropdownPos.top, left: dropdownPos.left,
            width: dropdownPos.width, zIndex: 99999,
            background: 'white', border: '1px solid #ccc', borderRadius: '0 0 4px 4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', display: 'flex', flexDirection: 'column'
          }}>
          <input
            autoFocus
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Escriba para buscar..."
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: '6px 8px', border: 'none', borderBottom: '1px solid #eee',
              fontSize: '12px', outline: 'none', flexShrink: 0
            }}
          />
          <div style={{ overflowY: 'auto', maxHeight: '160px' }}>
            {value && (
              <div
                onClick={() => { onChange(''); setAbierto(false); }}
                style={{
                  padding: '5px 8px', fontSize: '11px', cursor: 'pointer',
                  color: '#999', fontStyle: 'italic', borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >
                — Limpiar selección —
              </div>
            )}
            {filtradas.length === 0 ? (
              <div style={{ padding: '8px', fontSize: '11px', color: '#999', textAlign: 'center' }}>
                Sin resultados
              </div>
            ) : (
              filtradas.map(o => (
                <div
                  key={o.value}
                  onClick={() => { onChange(String(o.value)); setAbierto(false); setTexto(''); }}
                  style={{
                    padding: '5px 8px', fontSize: '11px', cursor: 'pointer',
                    background: String(o.value) === String(value) ? '#e3f2fd' : 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                  onMouseLeave={(e) => e.target.style.background = String(o.value) === String(value) ? '#e3f2fd' : 'white'}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Obtener lunes y domingo de la semana actual
const getSemanaActual = () => {
  const hoy = new Date();
  const dia = hoy.getDay(); // 0=dom, 1=lun...
  const diffLunes = dia === 0 ? -6 : 1 - dia;
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() + diffLunes);
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  return {
    desde: lunes.toISOString().split('T')[0],
    hasta: domingo.toISOString().split('T')[0]
  };
};

const NuevaCompraModal = ({ isOpen, onClose, embedded = false }) => {
  const semana = getSemanaActual();
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(semana.desde);
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(semana.hasta);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  // Modal nueva compra
  const [showModal, setShowModal] = useState(false);
  const [formulario, setFormulario] = useState({
    idProveedor: '',
    referencia: '',
    fecha: '',
    status: 'PENDIENTE'
  });
  const [detalles, setDetalles] = useState([]);

  // Modal detalle compra
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [compraDetalle, setCompraDetalle] = useState(null);
  const [detallesCompra, setDetallesCompra] = useState([]);
  const [abonosCompra, setAbonosCompra] = useState([]);

  // Modal abonar
  const [showAbonoModal, setShowAbonoModal] = useState(false);
  const [compraAbonar, setCompraAbonar] = useState(null);
  const [formAbono, setFormAbono] = useState({ monto: '', referencia: '' });
  const [guardandoAbono, setGuardandoAbono] = useState(false);

  // Modal recalcular costos platillos
  const [showRecalcularModal, setShowRecalcularModal] = useState(false);
  const [ingredientesCompra, setIngredientesCompra] = useState([]);
  const [recalculando, setRecalculando] = useState(false);
  const [resultadoRecalculo, setResultadoRecalculo] = useState(null);

  const toast = useToast();

  useEffect(() => {
    if (isOpen || embedded) {
      cargarCompras();
      cargarProveedores();
      cargarIngredientes();
    }
  }, [isOpen]);

  const apiFetch = async (tipo, datos = {}) => {
    const response = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, ...datos })
    });
    return response.json();
  };

  const cargarCompras = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('getCompras', { filtroFecha: 'todas' });
      if (data.success) {
        setCompras(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar compras');
      }
    } catch (error) {
      console.error('Error al cargar compras:', error);
      toast.error('Error al cargar compras');
    } finally {
      setLoading(false);
    }
  };

  const cargarProveedores = async () => {
    try {
      const data = await apiFetch('getProveedores');
      if (data.success) setProveedores(data.data || []);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarIngredientes = async () => {
    try {
      const data = await apiFetch('getTodosLosIngredientes');
      if (data.success) setIngredientes(data.data || []);
    } catch (error) {
      console.error('Error al cargar ingredientes:', error);
    }
  };

  // --- Filtrar compras ---
  const hayFiltrosActivos = filtroProveedor || filtroEstatus || filtroFechaDesde || filtroFechaHasta;

  const limpiarFiltros = () => {
    setFiltroProveedor('');
    setFiltroEstatus('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setBusqueda('');
  };

  const comprasFiltradas = compras.filter(compra => {
    // Búsqueda de texto
    if (busqueda) {
      const texto = busqueda.toLowerCase();
      const coincide =
        compra.compra?.toLowerCase().includes(texto) ||
        compra.referencia?.toLowerCase().includes(texto) ||
        compra.proveedor?.toLowerCase().includes(texto);
      if (!coincide) return false;
    }

    // Filtro proveedor
    if (filtroProveedor && compra.proveedor !== filtroProveedor) return false;

    // Filtro estatus
    if (filtroEstatus) {
      if (filtroEstatus === 'PENDIENTES' && compra.estatus !== 'PENDIENTE' && compra.estatus !== 'PARCIAL') return false;
      if (filtroEstatus === 'PAGADOS' && compra.estatus !== 'PAGADO') return false;
      if (filtroEstatus !== 'PENDIENTES' && filtroEstatus !== 'PAGADOS' && compra.estatus !== filtroEstatus) return false;
    }

    // Filtro fechas (la fecha viene como DD/MM/YYYY del API)
    if (filtroFechaDesde || filtroFechaHasta) {
      const partes = compra.fecha?.split('/');
      if (partes && partes.length === 3) {
        const fechaCompra = `${partes[2]}-${partes[1]}-${partes[0]}`; // YYYY-MM-DD
        if (filtroFechaDesde && fechaCompra < filtroFechaDesde) return false;
        if (filtroFechaHasta && fechaCompra > filtroFechaHasta) return false;
      }
    }

    return true;
  });

  // Proveedores únicos para el filtro
  const proveedoresUnicos = [...new Set(compras.map(c => c.proveedor).filter(Boolean))].sort();

  // --- Nueva compra ---
  const nuevaCompra = () => {
    setFormulario({
      idProveedor: '',
      referencia: '',
      fecha: new Date().toISOString().split('T')[0],
      status: 'PENDIENTE'
    });
    setDetalles([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormulario({ idProveedor: '', referencia: '', fecha: '', status: 'PENDIENTE' });
    setDetalles([]);
  };

  const agregarDetalle = () => {
    setDetalles([...detalles, { idIngrediente: '', nombre: '', cantidad: '', costo: '', unidad: '' }]);
  };

  const actualizarDetalle = (index, campo, valor) => {
    const nuevos = [...detalles];
    nuevos[index][campo] = valor;

    // Si selecciona ingrediente, autocompletar nombre, costo y unidad
    if (campo === 'idIngrediente') {
      const ing = ingredientes.find(i => i.idIngrediente == valor);
      if (ing) {
        nuevos[index].nombre = ing.nombre;
        nuevos[index].costo = ing.costo || 0;
        nuevos[index].unidad = ing.prefijoUnidad || ing.nombreUnidad || '';
      } else {
        nuevos[index].unidad = '';
      }
    }

    // Si cambia el importe, recalcular costo unitario
    if (campo === 'importe') {
      const cantidad = parseFloat(nuevos[index].cantidad) || 0;
      if (cantidad > 0) {
        nuevos[index].costo = (parseFloat(valor) || 0) / cantidad;
      }
    }

    // Si cambia cantidad y hay importe manual, recalcular costo unitario en base al importe
    if (campo === 'cantidad' && nuevos[index].importe !== undefined && nuevos[index].importe !== '') {
      const importe = parseFloat(nuevos[index].importe) || 0;
      const cantidad = parseFloat(valor) || 0;
      if (cantidad > 0) {
        nuevos[index].costo = importe / cantidad;
      }
    }

    // Si cambia costo unitario directamente, limpiar importe manual
    if (campo === 'costo') {
      nuevos[index].importe = '';
    }

    setDetalles(nuevos);
  };

  const eliminarDetalle = (index) => {
    setDetalles(detalles.filter((_, i) => i !== index));
  };

  const calcularTotalCompra = () => {
    return detalles.reduce((sum, d) => sum + (parseFloat(d.cantidad) || 0) * (parseFloat(d.costo) || 0), 0);
  };

  const guardarCompra = async () => {
    if (!formulario.idProveedor) {
      toast.error('Seleccione un proveedor');
      return;
    }
    if (!formulario.referencia.trim()) {
      toast.error('Ingrese una referencia');
      return;
    }
    if (!formulario.fecha) {
      toast.error('Seleccione una fecha');
      return;
    }
    if (detalles.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }

    const detallesValidos = detalles.filter(d => d.idIngrediente && parseFloat(d.cantidad) > 0);
    if (detallesValidos.length === 0) {
      toast.error('Complete al menos un producto con ingrediente y cantidad');
      return;
    }

    try {
      setGuardando(true);
      const monto = calcularTotalCompra();
      const data = await apiFetch('crearCompra', {
        idProveedor: parseInt(formulario.idProveedor),
        referencia: formulario.referencia.trim(),
        fecha: formulario.fecha,
        monto: monto,
        status: formulario.status,
        detalles: detallesValidos.map(d => ({
          idIngrediente: parseInt(d.idIngrediente),
          nombre: d.nombre,
          cantidad: parseFloat(d.cantidad),
          costo: parseFloat(d.costo) || 0
        }))
      });

      if (data.success) {
        toast.success('Compra creada correctamente');
        // Guardar IDs de ingredientes afectados antes de cerrar el modal
        const idsIngredientes = detallesValidos.map(d => parseInt(d.idIngrediente)).filter(id => id > 0);
        handleCloseModal();
        cargarCompras();
        // Preguntar si desea recalcular costos de platillos afectados
        if (idsIngredientes.length > 0) {
          setIngredientesCompra(idsIngredientes);
          setResultadoRecalculo(null);
          setShowRecalcularModal(true);
        }
      } else {
        toast.error(data.message || 'Error al crear la compra');
      }
    } catch (error) {
      console.error('Error al guardar compra:', error);
      toast.error('Error al guardar la compra');
    } finally {
      setGuardando(false);
    }
  };

  // --- Ver detalle de compra ---
  const verDetalle = async (compra) => {
    setCompraDetalle(compra);
    setShowDetalleModal(true);
    try {
      const [detData, aboData] = await Promise.all([
        apiFetch('getCompraDetalle', { idCompra: compra.id }),
        apiFetch('getAbonosCompra', { idCompra: compra.id })
      ]);
      if (detData.success) setDetallesCompra(detData.data || []);
      if (aboData.success) setAbonosCompra(aboData.data || []);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  };

  // --- Abonar ---
  const abrirAbonoModal = (compra) => {
    setCompraAbonar(compra);
    setFormAbono({ monto: '', referencia: '' });
    setShowAbonoModal(true);
  };

  const guardarAbono = async () => {
    const monto = parseFloat(formAbono.monto);
    if (!monto || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    if (monto > compraAbonar.saldo) {
      toast.error(`El monto no puede ser mayor al saldo ($${compraAbonar.saldo.toFixed(2)})`);
      return;
    }

    try {
      setGuardandoAbono(true);
      const data = await apiFetch('crearAbono', {
        idCompra: compraAbonar.id,
        monto: monto,
        referencia: formAbono.referencia.trim(),
        fecha: new Date().toISOString().split('T')[0]
      });

      if (data.success) {
        toast.success('Abono registrado correctamente');
        setShowAbonoModal(false);
        cargarCompras();
      } else {
        toast.error(data.message || 'Error al registrar abono');
      }
    } catch (error) {
      console.error('Error al abonar:', error);
      toast.error('Error al registrar abono');
    } finally {
      setGuardandoAbono(false);
    }
  };

  // --- Recalcular costos de platillos ---
  const recalcularCostos = async () => {
    try {
      setRecalculando(true);
      const data = await apiFetch('recalcularCostoPlatillos', {
        ingredientesIds: ingredientesCompra
      });

      if (data.success) {
        setResultadoRecalculo(data.data);
        const totalActualizados = (data.data.preparadosActualizados || 0) + (data.data.platillosActualizados || 0);
        toast.success(`Se actualizaron ${data.data.preparadosActualizados || 0} preparado(s) y ${data.data.platillosActualizados || 0} platillo(s)`);
      } else {
        toast.error(data.message || 'Error al recalcular costos');
        setShowRecalcularModal(false);
      }
    } catch (error) {
      console.error('Error al recalcular costos:', error);
      toast.error('Error al recalcular costos de platillos');
      setShowRecalcularModal(false);
    } finally {
      setRecalculando(false);
    }
  };

  // --- Status badge ---
  const getStatusClass = (estatus) => {
    switch (estatus) {
      case 'PENDIENTE': return { background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' };
      case 'PAGADO': return { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' };
      case 'PARCIAL': return { background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' };
      default: return {};
    }
  };

  const contenido = (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar compras..."
                className="excel-search-input"
              />
              {busqueda && (
                <button onClick={() => setBusqueda('')} className="excel-clear-search">
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="btn-excel"
              style={{ background: mostrarFiltros ? 'linear-gradient(135deg, #FF9800, #F57C00)' : 'linear-gradient(135deg, #607D8B, #455A64)' }}
            >
              <FaFilter /> Filtros {hayFiltrosActivos ? '*' : ''}
            </button>
            <button onClick={nuevaCompra} className="btn-excel btn-nuevo">
              <FaPlus /> Nueva Compra
            </button>
          </div>
        </div>
      </div>

      {/* Barra de filtros */}
      {mostrarFiltros && (
        <div style={{
          display: 'flex', gap: '10px', alignItems: 'flex-end', padding: '10px 16px',
          background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
            <label style={{ fontWeight: '600', color: '#555' }}>Proveedor</label>
            <select
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', minWidth: '180px' }}
            >
              <option value="">Todos</option>
              {proveedoresUnicos.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
            <label style={{ fontWeight: '600', color: '#555' }}>Estatus</label>
            <select
              value={filtroEstatus}
              onChange={(e) => setFiltroEstatus(e.target.value)}
              style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px', minWidth: '130px' }}
            >
              <option value="">Todos</option>
              <option value="PENDIENTES">Pendientes</option>
              <option value="PAGADOS">Pagados</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PARCIAL">Parcial</option>
              <option value="PAGADO">Pagado</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
            <label style={{ fontWeight: '600', color: '#555' }}>Fecha desde</label>
            <input
              type="date"
              value={filtroFechaDesde}
              onChange={(e) => setFiltroFechaDesde(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
            <label style={{ fontWeight: '600', color: '#555' }}>Fecha hasta</label>
            <input
              type="date"
              value={filtroFechaHasta}
              onChange={(e) => setFiltroFechaHasta(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>
          {hayFiltrosActivos && (
            <button
              onClick={limpiarFiltros}
              className="btn-excel btn-eliminar"
              style={{ padding: '5px 10px', fontSize: '11px' }}
            >
              <FaEraser /> Limpiar
            </button>
          )}
        </div>
      )}

      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">No. Compra</th>
              <th className="excel-th">Fecha</th>
              <th className="excel-th">Referencia</th>
              <th className="excel-th">Proveedor</th>
              <th className="excel-th">Monto</th>
              <th className="excel-th">Abonado</th>
              <th className="excel-th">Saldo</th>
              <th className="excel-th">Estatus</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="excel-loading">Cargando compras...</td>
              </tr>
            ) : comprasFiltradas.length === 0 ? (
              <tr>
                <td colSpan="9" className="excel-empty">
                  {busqueda ? `No se encontraron compras para "${busqueda}"` : 'No hay compras registradas'}
                </td>
              </tr>
            ) : (
              comprasFiltradas.map(compra => (
                <tr key={compra.id} className="excel-row">
                  <td className="excel-cell excel-cell-nombre">{compra.compra}</td>
                  <td className="excel-cell">{compra.fecha}</td>
                  <td className="excel-cell">{compra.referencia}</td>
                  <td className="excel-cell">{compra.proveedor}</td>
                  <td className="excel-cell text-right">${compra.monto.toFixed(2)}</td>
                  <td className="excel-cell text-right">${compra.abonado.toFixed(2)}</td>
                  <td className="excel-cell text-right">${compra.saldo.toFixed(2)}</td>
                  <td className="excel-cell">
                    <span style={{
                      ...getStatusClass(compra.estatus),
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      {compra.estatus}
                    </span>
                  </td>
                  <td className="excel-cell excel-cell-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); verDetalle(compra); }}
                      className="excel-btn-action excel-btn-edit"
                      title="Ver detalle"
                    >
                      <FaEye />
                    </button>
                    {compra.saldo > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); abrirAbonoModal(compra); }}
                        className="excel-btn-action"
                        style={{ background: '#4CAF50', color: 'white' }}
                        title="Abonar"
                      >
                        <FaDollarSign />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de compras: <strong>{comprasFiltradas.length}</strong></span>
          {busqueda && <span> de <strong>{compras.length}</strong></span>}
        </div>
        <div className="excel-shortcuts">
          <span style={{ fontWeight: '600' }}>
            Total: ${comprasFiltradas.reduce((s, c) => s + c.monto, 0).toFixed(2)} |
            Saldo: ${comprasFiltradas.reduce((s, c) => s + c.saldo, 0).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Modal nueva compra */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Nueva Compra"
        onSave={guardarCompra}
        saveLabel={guardando ? 'Guardando...' : 'Crear Compra'}
        isSaving={guardando}
        size="large"
        saveIcon={<FaShoppingCart />}
        className="modal-ajuste-inventario"
      >
        <FormGrid columns={4} gap="10px">
          <div className="form-field-container">
            <label className="form-field-label">Proveedor <span style={{ color: 'red' }}>*</span></label>
            <SearchableSelect
              options={proveedores.map(p => ({ value: p.idProveedor, label: p.nombre }))}
              value={formulario.idProveedor}
              onChange={(val) => setFormulario({ ...formulario, idProveedor: val })}
              placeholder="Seleccione proveedor"
            />
          </div>
          <FormField
            label="Referencia"
            name="referencia"
            value={formulario.referencia}
            onChange={(e) => setFormulario({ ...formulario, referencia: e.target.value })}
            placeholder="Factura / Remisión"
            required
          />
          <FormField
            label="Fecha"
            type="date"
            name="fecha"
            value={formulario.fecha}
            onChange={(e) => setFormulario({ ...formulario, fecha: e.target.value })}
            required
          />
          <FormField
            label="Estatus"
            type="select"
            name="status"
            value={formulario.status}
            onChange={(e) => setFormulario({ ...formulario, status: e.target.value })}
            options={[
              { value: 'PENDIENTE', label: 'Pendiente' },
              { value: 'PAGADO', label: 'Pagado' }
            ]}
          />
        </FormGrid>

        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <strong style={{ fontSize: '13px' }}>Productos</strong>
            <button
              type="button"
              onClick={agregarDetalle}
              className="btn-excel btn-nuevo"
              style={{ padding: '4px 10px', fontSize: '11px' }}
            >
              <FaPlus /> Agregar
            </button>
          </div>
          <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table className="excel-table" style={{ fontSize: '11px' }}>
              <thead>
                <tr>
                  <th className="excel-th">Ingrediente</th>
                  <th className="excel-th" style={{ width: '80px' }}>Unidad</th>
                  <th className="excel-th" style={{ width: '100px' }}>Cantidad</th>
                  <th className="excel-th" style={{ width: '100px' }}>Costo Unit.</th>
                  <th className="excel-th" style={{ width: '100px' }}>Importe</th>
                  <th className="excel-th" style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {detalles.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '16px', color: '#999', fontStyle: 'italic' }}>
                      Click en "Agregar" para añadir productos
                    </td>
                  </tr>
                ) : (
                  detalles.map((det, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '2px 4px' }}>
                        <SearchableSelect
                          options={ingredientes.map(i => ({ value: i.idIngrediente, label: i.nombre }))}
                          value={det.idIngrediente}
                          onChange={(val) => actualizarDetalle(idx, 'idIngrediente', val)}
                          placeholder="Buscar ingrediente..."
                          style={{ fontSize: '11px' }}
                        />
                      </td>
                      <td style={{ padding: '2px 4px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#555' }}>
                        {det.unidad || '-'}
                      </td>
                      <td style={{ padding: '2px 4px' }}>
                        <input
                          type="number"
                          value={det.cantidad}
                          onChange={(e) => actualizarDetalle(idx, 'cantidad', e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0"
                          style={{ width: '100%', padding: '4px', fontSize: '11px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                      </td>
                      <td style={{ padding: '2px 4px' }}>
                        <input
                          type="number"
                          value={det.costo}
                          onChange={(e) => actualizarDetalle(idx, 'costo', e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          style={{ width: '100%', padding: '4px', fontSize: '11px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                      </td>
                      <td style={{ padding: '2px 4px' }}>
                        <input
                          type="number"
                          value={det.importe !== undefined && det.importe !== '' ? det.importe : ((parseFloat(det.cantidad) || 0) * (parseFloat(det.costo) || 0)).toFixed(2)}
                          onChange={(e) => actualizarDetalle(idx, 'importe', e.target.value)}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          style={{ width: '100%', padding: '4px', fontSize: '11px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '3px' }}
                        />
                      </td>
                      <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => eliminarDetalle(idx)}
                          className="excel-btn-action excel-btn-delete"
                          style={{ padding: '2px 4px' }}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {detalles.length > 0 && (
            <div style={{ textAlign: 'right', marginTop: '8px', fontSize: '14px', fontWeight: '600' }}>
              Total: ${calcularTotalCompra().toFixed(2)}
            </div>
          )}
        </div>
      </FormModal>

      {/* Modal detalle de compra */}
      <FormModal
        isOpen={showDetalleModal}
        onClose={() => { setShowDetalleModal(false); setCompraDetalle(null); setDetallesCompra([]); setAbonosCompra([]); }}
        title={`Detalle de Compra ${compraDetalle?.compra || ''}`}
        size="large"
        showCancel={false}
        saveLabel="Cerrar"
        onSave={() => { setShowDetalleModal(false); setCompraDetalle(null); }}
        saveIcon={<FaTimes />}
      >
        {compraDetalle && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
              <div><strong>Proveedor:</strong> {compraDetalle.proveedor}</div>
              <div><strong>Fecha:</strong> {compraDetalle.fecha}</div>
              <div><strong>Referencia:</strong> {compraDetalle.referencia}</div>
              <div><strong>Estatus:</strong> <span style={{ ...getStatusClass(compraDetalle.estatus), padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>{compraDetalle.estatus}</span></div>
            </div>
            <strong style={{ fontSize: '12px' }}>Productos:</strong>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', marginBottom: '12px' }}>
              <table className="excel-table" style={{ fontSize: '11px' }}>
                <thead>
                  <tr>
                    <th className="excel-th">Ingrediente</th>
                    <th className="excel-th">Cantidad</th>
                    <th className="excel-th">Costo</th>
                    <th className="excel-th">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {detallesCompra.map(d => (
                    <tr key={d.idCompraDetalle}>
                      <td className="excel-cell">{d.nombre}</td>
                      <td className="excel-cell text-right">{d.cantidad.toFixed(2)}</td>
                      <td className="excel-cell text-right">${d.costo.toFixed(2)}</td>
                      <td className="excel-cell text-right">${d.importe.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
              <div>Monto: ${compraDetalle.monto.toFixed(2)}</div>
              <div style={{ color: '#2e7d32' }}>Abonado: ${compraDetalle.abonado.toFixed(2)}</div>
              <div style={{ color: '#c62828' }}>Saldo: ${compraDetalle.saldo.toFixed(2)}</div>
            </div>
            {abonosCompra.length > 0 && (
              <>
                <strong style={{ fontSize: '12px' }}>Abonos:</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px' }}>
                  <table className="excel-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th className="excel-th">Fecha</th>
                        <th className="excel-th">Monto</th>
                        <th className="excel-th">Referencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abonosCompra.map(a => (
                        <tr key={a.idAbono}>
                          <td className="excel-cell">{a.fecha}</td>
                          <td className="excel-cell text-right">${a.monto.toFixed(2)}</td>
                          <td className="excel-cell">{a.referencia}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </FormModal>

      {/* Modal abonar */}
      <FormModal
        isOpen={showAbonoModal}
        onClose={() => setShowAbonoModal(false)}
        title={`Abonar a ${compraAbonar?.compra || ''}`}
        onSave={guardarAbono}
        saveLabel="Registrar Abono"
        isSaving={guardandoAbono}
        size="small"
        saveIcon={<FaDollarSign />}
      >
        {compraAbonar && (
          <>
            <div style={{ marginBottom: '12px', fontSize: '12px', background: '#f5f5f5', padding: '10px', borderRadius: '6px' }}>
              <div><strong>Proveedor:</strong> {compraAbonar.proveedor}</div>
              <div><strong>Monto total:</strong> ${compraAbonar.monto.toFixed(2)}</div>
              <div><strong>Abonado:</strong> ${compraAbonar.abonado.toFixed(2)}</div>
              <div style={{ color: '#c62828', fontWeight: '600' }}><strong>Saldo pendiente:</strong> ${compraAbonar.saldo.toFixed(2)}</div>
            </div>
            <FormGrid columns={1} gap="10px">
              <FormField
                label="Monto del abono"
                type="number"
                name="monto"
                value={formAbono.monto}
                onChange={(e) => setFormAbono({ ...formAbono, monto: e.target.value })}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                max={compraAbonar.saldo}
                required
                icon={<FaDollarSign />}
              />
              <FormField
                label="Referencia"
                name="referencia"
                value={formAbono.referencia}
                onChange={(e) => setFormAbono({ ...formAbono, referencia: e.target.value })}
                placeholder="No. de recibo, transferencia, etc."
              />
            </FormGrid>
          </>
        )}
      </FormModal>

      {/* Modal recalcular costos de platillos */}
      <FormModal
        isOpen={showRecalcularModal}
        onClose={() => { setShowRecalcularModal(false); setResultadoRecalculo(null); }}
        title="Recalcular Costos de Platillos"
        onSave={resultadoRecalculo ? () => { setShowRecalcularModal(false); setResultadoRecalculo(null); } : recalcularCostos}
        saveLabel={recalculando ? 'Recalculando...' : resultadoRecalculo ? 'Cerrar' : 'Sí, Recalcular'}
        isSaving={recalculando}
        size={resultadoRecalculo ? 'medium' : 'small'}
        saveIcon={resultadoRecalculo ? <FaTimes /> : <FaDollarSign />}
        cancelLabel={resultadoRecalculo ? undefined : 'No, Omitir'}
        showCancel={!resultadoRecalculo}
      >
        {!resultadoRecalculo ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>💰</div>
            <p style={{ fontSize: '14px', marginBottom: '8px' }}>
              Los costos de los ingredientes han sido actualizados con esta compra.
            </p>
            <p style={{ fontSize: '13px', color: '#555' }}>
              ¿Desea recalcular los costos de los platillos que utilizan estos ingredientes?
            </p>
          </div>
        ) : (
          <div>
            <div style={{
              background: '#e8f5e9', padding: '10px 14px', borderRadius: '6px',
              marginBottom: '12px', fontSize: '13px', color: '#2e7d32', fontWeight: '600'
            }}>
              Se actualizaron {resultadoRecalculo.preparadosActualizados || 0} preparado(s) y {resultadoRecalculo.platillosActualizados || 0} platillo(s)
            </div>
            {resultadoRecalculo.detallePreparados && resultadoRecalculo.detallePreparados.length > 0 && (
              <>
                <strong style={{ fontSize: '12px' }}>Preparados:</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', marginBottom: '12px' }}>
                  <table className="excel-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th className="excel-th">Preparado</th>
                        <th className="excel-th" style={{ width: '100px' }}>Nuevo Costo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoRecalculo.detallePreparados.map(p => (
                        <tr key={p.idPreparado}>
                          <td className="excel-cell">{p.nombre}</td>
                          <td className="excel-cell text-right">${p.nuevoCosto.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {resultadoRecalculo.detallePlatillos && resultadoRecalculo.detallePlatillos.length > 0 && (
              <>
                <strong style={{ fontSize: '12px' }}>Platillos:</strong>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px' }}>
                  <table className="excel-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th className="excel-th">Platillo</th>
                        <th className="excel-th" style={{ width: '100px' }}>Anterior</th>
                        <th className="excel-th" style={{ width: '100px' }}>Nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoRecalculo.detallePlatillos.map(p => (
                        <tr key={p.idPlatillo}>
                          <td className="excel-cell">{p.nombre}</td>
                          <td className="excel-cell text-right" style={{ color: '#999' }}>${p.costoAnterior.toFixed(2)}</td>
                          <td className="excel-cell text-right" style={{ fontWeight: '600', color: p.nuevoCosto !== p.costoAnterior ? '#c62828' : '#333' }}>${p.nuevoCosto.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(!resultadoRecalculo.detallePlatillos || resultadoRecalculo.detallePlatillos.length === 0) &&
             (!resultadoRecalculo.detallePreparados || resultadoRecalculo.detallePreparados.length === 0) && (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>
                No se encontraron platillos ni preparados que utilicen estos ingredientes.
              </p>
            )}
          </div>
        )}
      </FormModal>
    </div>
  );

  if (embedded) {
    return contenido;
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Compras"
      icon={<FaShoppingCart />}
      maxHeight="90vh"
      showCancel={false}
      saveLabel="Cerrar"
      onSave={onClose}
    >
      {contenido}
    </FormModal>
  );
};

export default NuevaCompraModal;
